import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Spinner,
  Modal,
  Row,
  Col,
  Form,
  Badge,
  Pagination,
} from "react-bootstrap";
import {
  CalendarEvent,
  PeopleFill,
  GeoAltFill,
  Search,
  CheckCircleFill,
  XCircleFill,
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
  Paper,
} from "@mui/material";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const getAvailabilityLabel = (cls) => {
  if (cls?.isFullyBooked) return "Fully booked";
  if (cls?.maxParticipants == null) return "Unlimited spots";
  return `${cls?.registrationCount || 0} / ${cls.maxParticipants} spots filled`;
};

const formatSessionDate = (date) => {
  if (!date) return "N/A";
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime())
    ? "N/A"
    : parsedDate.toLocaleDateString();
};

const getAttendanceSessionDate = (item) =>
  item?.sessionDate ||
  item?.date ||
  item?.session?.date ||
  item?.classSession?.date ||
  item?.sessionId?.date;

const getClassSessionDate = (cls) =>
  cls?.sessionDate ||
  cls?.date ||
  cls?.nextSession?.date ||
  cls?.sessions?.[0]?.date;

const getEligibleParticipantCount = (item, fallbackCount) => {
  const count =
    item?.eligibleParticipantCount ??
    item?.eligibleParticipants ??
    item?.totalEligibleUsers ??
    item?.totalUsers ??
    item?.participantCount ??
    item?.participantsCount ??
    item?.registrationCount ??
    item?.totalParticipants ??
    item?.totalRegistrations;

  if (count != null) return count;
  if (Array.isArray(item?.participants)) return item.participants.length;
  if (Array.isArray(item?.registeredUsers)) return item.registeredUsers.length;
  return fallbackCount || 0;
};

const getModalParticipantTotal = (items, paginationTotal) => {
  const firstItem = items?.[0];
  return getEligibleParticipantCount(
    firstItem,
    paginationTotal || items?.length || 0,
  );
};

const ClassAttendance = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [allLocations, setAllLocations] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);
  const [sessionPagination, setSessionPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendancePagination, setAttendancePagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [attLoading, setAttLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // ================= FETCH CLASSES =================
  const fetchClasses = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/api/AdminClasses/getAllClasses?page=${page}&limit=${limit}`;

      if (search) url += `&search=${search}`;
      if (filterLocation) url += `&filterLocation=${filterLocation}`;
      if (filterStatus) url += `&classStatus=${filterStatus}`;

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      setData(res.data.data || []);
      setPagination({
        page: res.data.page,
        limit: res.data.limit,
        total: res.data.total,
      });
    } catch (err) {
      console.error("Error fetching class data:", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH LOCATIONS =================
  const fetchAllLocations = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/location/admin/getAllLocations`,
      );
      setAllLocations(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  useEffect(() => {
    fetchAllLocations();
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchClasses(pagination.page, pagination.limit);
  }, [search, filterLocation, filterStatus]);

  const handlePageChange = (page) => {
    fetchClasses(page, pagination.limit);
  };

  // ================= SHOW SESSIONS =================
  const handleShowSessions = async (cls, page = 1, limit = 10) => {
    try {
      setSelectedClass(cls);
      setShowSessionsModal(true);
      setSessionsLoading(true);

      const res = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getClassAttendence/${cls._id}?page=${page}&limit=${limit}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );
      setSessionsData(res?.data?.data || []);
      setSessionPagination({
        page: res.data?.page || page,
        limit: res.data?.limit || limit,
        total:
          res.data?.total ||
          res.data?.totalUsers ||
          res.data?.totalParticipants ||
          res.data?.totalSessions ||
          0,
      });
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setSessionsData([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleSessionChange = (page) => {
    if (selectedClass) {
      handleShowSessions(selectedClass, page, sessionPagination.limit);
    }
  };

  // ================= SHOW ATTENDANCE =================
  const handleShowAttendance = async (session, page = 1, limit = 10) => {
    try {
      setSelectedSession(session);
      setShowSessionsModal(false);
      setShowAttendanceModal(true);
      setAttLoading(true);

      setAttendancePagination((prev) => ({
        ...prev,
        page: page,
        limit: limit,
      }));

      const res = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getClassById/${selectedClass._id}/${session._id}?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );

      const registeredUsers = res.data?.data || [];
      const users = registeredUsers.map((user) => ({
        id: user.userId,
        name: user.name || "Unknown",
        email: user.email || "N/A",
        attendance: user.attendance || "Absent",
        isPrisoner: false,
      }));

      const prisonersRaw =
        res.data?.data?.instructorAttendance?.prisonerAttendance || [];

      const prisoners = prisonersRaw.map((p) => ({
        id: p.prisonerId,
        name: p.name || "Unknown",
        email: "—",
        attendance: p.status,
        isPrisoner: true,
      }));

      setAttendanceData([...users, ...prisoners]);
      setAttendancePagination({
        page: res.data?.page || page,
        limit: res.data?.limit || limit,
        total: res.data?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setAttendanceData([]);
    } finally {
      setAttLoading(false);
    }
  };

  // ================= HANDLE ATTENDANCE PAGE CHANGE =================
  const handleAttendanceChange = (page) => {
    if (selectedClass && selectedSession) {
      handleShowAttendance(selectedSession, page, attendancePagination.limit);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const sessionTotalPages = Math.ceil(
    sessionPagination.total / sessionPagination.limit,
  );
  const attendanceTotalPages = Math.ceil(
    attendancePagination.total / attendancePagination.limit,
  );

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Class Attendance
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <CalendarEvent size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Class Attendance
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {pagination.total} total classes
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search by title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          </Col>

          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <option value="">All Locations</option>
              {allLocations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.location}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="ended">Ended</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Main Table */}
      <Paper
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading classes...
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box py={5} textAlign="center">
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              No classes found.
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {[
                  "#",
                  "Title",
                  "Date",
                  "Location",
                  "Instructor",
                  "Registrations",
                  "Availability",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((cls, idx) => {
                const rawStatus = cls.classStatus?.toLowerCase() || "upcoming";

                return (
                  <tr
                    key={cls._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {(pagination.page - 1) * pagination.limit + idx + 1}
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {cls.title}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {formatSessionDate(getClassSessionDate(cls))}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      <GeoAltFill size={12} className="me-1" />
                      {cls.location?.location || "N/A"}
                    </td>
                    <td>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            color: "#1a237e",
                            display: "block",
                          }}
                        >
                          {cls.Instructor?.name || "N/A"}
                        </Typography>
                        {cls.Instructor?.email && (
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.7rem", color: "#7b8ab8" }}
                          >
                            {cls.Instructor.email}
                          </Typography>
                        )}
                      </Box>
                    </td>
                    <td>
                      <span
                        className="badge px-3 py-2 rounded-pill"
                        style={{
                          background: "#e4eaf8",
                          color: "#1a237e",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        <PeopleFill size={12} className="me-1" />
                        {cls.registrationCount || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          cls.isFullyBooked ? "bg-danger" : ""
                        }`}
                        style={{
                          background: cls.isFullyBooked ? undefined : "#e4eaf8",
                          color: cls.isFullyBooked ? undefined : "#1a237e",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {getAvailabilityLabel(cls)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          rawStatus === "live"
                            ? "bg-success"
                            : rawStatus === "upcoming"
                              ? "bg-warning text-dark"
                              : "bg-secondary"
                        }`}
                        style={{ fontSize: "0.75rem" }}
                      >
                        {rawStatus.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {/* FIX: was handleShowAttendance(cls) — selectedClass was null */}
                      <ActionBtn
                        tooltip="View Sessions"
                        color="#0d47a1"
                        onClick={() => handleShowSessions(cls)}
                      >
                        <CalendarEvent size={16} />
                      </ActionBtn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(pagination.page - 1) * pagination.limit + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{pagination.total}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === pagination.page}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pageNum === pagination.page
                        ? {
                            backgroundColor: "#1a237e",
                            borderColor: "#1a237e",
                            color: "#fff",
                          }
                        : { border: "none", background: "transparent" }
                    }
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
            </Pagination>
          </Box>
        )}
      </Paper>

      {/* Sessions Modal */}
      <Modal
        show={showSessionsModal}
        onHide={() => setShowSessionsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <CalendarEvent size={18} /> Attendance for {selectedClass?.title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {sessionsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <Spinner animation="border" style={{ color: "#0d47a1" }} />
            </Box>
          ) : sessionsData ? (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap="wrap"
                gap={1}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#1a237e", fontWeight: 700 }}
                >
                  Session Date:{" "}
                  {formatSessionDate(
                    getAttendanceSessionDate(sessionsData[0]) ||
                      getClassSessionDate(selectedClass),
                  )}
                </Typography>
                <Badge
                  bg="light"
                  text="dark"
                  className="px-3 py-2 rounded-pill"
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    border: "1px solid #dde4f5",
                  }}
                >
                  <PeopleFill size={12} className="me-1" />
                  Total Participants:{" "}
                  {getModalParticipantTotal(
                    sessionsData,
                    sessionPagination.total,
                  )}
                </Badge>
              </Box>
              <Table hover className="mb-0 align-middle">
                <thead style={{ background: primaryGradient }}>
                  <tr>
                    {[
                      "#",
                      "Session Date",
                      "Name",
                      "Type",
                      "Email",
                      "Attendance",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          color: "rgba(255,255,255,0.85)",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          padding: "10px 12px",
                          border: "none",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessionsData.map((session, index) => (
                    <tr key={session._id}>
                      <td>
                        {index +
                          1 +
                          (sessionPagination.page - 1) *
                            sessionPagination.limit}
                      </td>
                      <td style={{ fontSize: "0.82rem", color: "#1a237e" }}>
                        {formatSessionDate(
                          getAttendanceSessionDate(session) ||
                            getClassSessionDate(selectedClass),
                        )}
                      </td>
                      <td style={{ fontSize: "0.82rem", color: "#1a237e" }}>
                        {session?.name || "N/A"}
                      </td>
                      <td style={{ fontSize: "0.82rem", color: "#1a237e" }}>
                        {session?.type?.toLowerCase() === "prisoner"
                          ? "student"
                          : session?.type || "N/A"}
                      </td>
                      <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                        {session?.email || "N/A"}
                      </td>
                      <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                        {session.attendance || "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {sessionPagination.total > sessionPagination.limit && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination size="sm">
                    <Pagination.Prev
                      disabled={sessionPagination.page === 1}
                      onClick={() =>
                        handleSessionChange(sessionPagination.page - 1)
                      }
                      style={{ border: "none" }}
                    />
                    {Array.from(
                      { length: Math.min(5, sessionTotalPages) },
                      (_, i) => {
                        let pageNum;
                        if (sessionTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (sessionPagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (
                          sessionPagination.page >=
                          sessionTotalPages - 2
                        ) {
                          pageNum = sessionTotalPages - 4 + i;
                        } else {
                          pageNum = sessionPagination.page - 2 + i;
                        }
                        return (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === sessionPagination.page}
                            onClick={() => handleSessionChange(pageNum)}
                            style={
                              pageNum === sessionPagination.page
                                ? {
                                    backgroundColor: "#1a237e",
                                    borderColor: "#1a237e",
                                    color: "#fff",
                                  }
                                : {}
                            }
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      },
                    )}
                    <Pagination.Next
                      disabled={sessionPagination.page === sessionTotalPages}
                      onClick={() =>
                        handleSessionChange(sessionPagination.page + 1)
                      }
                      style={{ border: "none" }}
                    />
                  </Pagination>
                </Box>
              )}
            </>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: "#7b8ab8",
                display: "block",
                textAlign: "center",
                py: 3,
              }}
            >
              No sessions found for this class.
            </Typography>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowSessionsModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Attendance Modal */}
      <Modal
        show={showAttendanceModal}
        onHide={() => {
          setShowAttendanceModal(false);
          setShowSessionsModal(true);
        }}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PeopleFill size={18} /> Attendance for {selectedClass?.title} –{" "}
            {selectedSession &&
              new Date(selectedSession.date).toLocaleDateString()}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {attLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <Spinner animation="border" style={{ color: "#0d47a1" }} />
            </Box>
          ) : attendanceData.length > 0 ? (
            <>
              <Table hover className="mb-0 align-middle">
                <thead style={{ background: primaryGradient }}>
                  <tr>
                    {["#", "Name", "Email", "Status"].map((h) => (
                      <th
                        key={h}
                        style={{
                          color: "rgba(255,255,255,0.85)",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          padding: "10px 12px",
                          border: "none",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {attendanceData.map((item, index) => (
                    <tr
                      key={item.id || index}
                      style={{ borderBottom: "1px solid #f0f4ff" }}
                    >
                      <td
                        style={{
                          color: "#aab4cc",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {(attendancePagination.page - 1) *
                          attendancePagination.limit +
                          index +
                          1}
                      </td>
                      <td>
                        {item.isPrisoner ? (
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 500, color: "#1a237e" }}
                          >
                            {item.name}
                          </Typography>
                        ) : (
                          <RouterLink
                            to={`/feedback/${selectedClass?._id}?userId=${item.id}`}
                            style={{
                              color: "#0d47a1",
                              textDecoration: "underline",
                              fontWeight: 500,
                              fontSize: "0.8rem",
                            }}
                          >
                            {item.name}
                          </RouterLink>
                        )}
                      </td>
                      <td style={{ fontSize: "0.75rem", color: "#6b7a99" }}>
                        {item.email}
                      </td>
                      <td>
                        <span
                          className={`badge px-3 py-2 rounded-pill ${
                            item.attendance === "Present"
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                          style={{ fontSize: "0.7rem" }}
                        >
                          {item.attendance === "Present" ? (
                            <CheckCircleFill size={12} className="me-1" />
                          ) : (
                            <XCircleFill size={12} className="me-1" />
                          )}
                          {item.attendance}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {attendancePagination.total > attendancePagination.limit && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination size="sm">
                    <Pagination.Prev
                      disabled={attendancePagination.page === 1}
                      onClick={() =>
                        handleAttendanceChange(attendancePagination.page - 1)
                      }
                      style={{ border: "none" }}
                    />

                    {Array.from(
                      { length: Math.min(5, attendanceTotalPages) },
                      (_, i) => {
                        let pageNum;
                        if (attendanceTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (attendancePagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (
                          attendancePagination.page >=
                          attendanceTotalPages - 2
                        ) {
                          pageNum = attendanceTotalPages - 4 + i;
                        } else {
                          pageNum = attendancePagination.page - 2 + i;
                        }
                        return (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === attendancePagination.page}
                            onClick={() => handleAttendanceChange(pageNum)}
                            style={
                              pageNum === attendancePagination.page
                                ? {
                                    backgroundColor: "#1a237e",
                                    borderColor: "#1a237e",
                                    color: "#fff",
                                  }
                                : {}
                            }
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      },
                    )}

                    <Pagination.Next
                      disabled={
                        attendancePagination.page === attendanceTotalPages
                      }
                      onClick={() =>
                        handleAttendanceChange(attendancePagination.page + 1)
                      }
                      style={{ border: "none" }}
                    />
                  </Pagination>
                </Box>
              )}
            </>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: "#7b8ab8",
                display: "block",
                textAlign: "center",
                py: 3,
              }}
            >
              No attendance data found for this session.
            </Typography>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAttendanceModal(false);
              setShowSessionsModal(true);
            }}
            style={{ borderRadius: "8px" }}
          >
            Back to Sessions
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default ClassAttendance;
